import React, { useState, useEffect } from "react";

export default function PlaceValuePuzzle() {
  const [currentNumberStr, setCurrentNumberStr] = useState("");
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [correctAnswer, setCorrectAnswer] = useState("");
  const [userAnswer, setUserAnswer] = useState("");
  const [feedback, setFeedback] = useState("");
  const [feedbackClass, setFeedbackClass] = useState("");

  // 🎉 Fun random success / failure messages
  const successMessages = [
    " Super! Well done!",
    " Great job, you nailed it!",
    " Awesome, you got it!",
    " Fantastic! Keep it up!",
    " Perfect! That’s correct!",
    " Yay, you cracked the code!",
  ];

  const failureMessages = [
    " Oops! Try again.",
    " Not quite, give it another shot!",
    " Hmm, that's not right!",
    " Wrong answer, but you can do it!",
  ];

  // 🔊 Funny Voice Speak
  const speak = (text, isCorrect) => {
    if ("speechSynthesis" in window) {
      const msg = new SpeechSynthesisUtterance(text);
      msg.pitch = isCorrect ? 1.6 : 0.6; // high pitch = funny happy, low = sad
      msg.rate = 1.1;
      msg.voice = speechSynthesis.getVoices()[0];
      window.speechSynthesis.cancel();
      window.speechSynthesis.speak(msg);
    }
  };

  useEffect(() => {
    generateNewNumber();
  }, []);

  useEffect(() => {
    if (currentNumberStr) {
      setQuestion(currentQuestionIndex);
    }
  }, [currentNumberStr, currentQuestionIndex]);

  const generateNumber = () => {
    return (Math.floor(Math.random() * 9000) + 1000).toString(); // 4-digit number
  };

  const generateNewNumber = () => {
    const newNumber = generateNumber();
    setCurrentNumberStr(newNumber);
    setCurrentQuestionIndex(0);
    setUserAnswer("");
    setFeedback("");
    setFeedbackClass("");
  };

  const setQuestion = (index) => {
    switch (index) {
      case 0: // Hundreds place digit
        setCorrectAnswer(currentNumberStr[1]);
        break;

      case 1: {
        // Swap hundreds and tens digits (index 1 and 2)
        const digits = [...currentNumberStr];
        [digits[1], digits[2]] = [digits[2], digits[1]];
        setCorrectAnswer(digits.join(""));
        break;
      }

      default:
        break;
    }

    setFeedback("");
    setFeedbackClass("");
    setUserAnswer("");
  };

  const checkAnswer = () => {
    if (userAnswer.trim() === "") {
      setFeedback("Please enter an answer.");
      setFeedbackClass("incorrect");
      return;
    }

    if (userAnswer.trim() === correctAnswer) {
      const randomMsg =
        successMessages[Math.floor(Math.random() * successMessages.length)];
      setFeedback(randomMsg);
      setFeedbackClass("correct");
      speak(randomMsg, true);
    } else {
      const randomMsg =
        failureMessages[Math.floor(Math.random() * failureMessages.length)];
      setFeedback(randomMsg);
      setFeedbackClass("incorrect");
      speak(randomMsg, false);
    }
  };

  const nextQuestion = () => {
    if (currentQuestionIndex >= 1) {
      generateNewNumber();
    } else {
      setCurrentQuestionIndex((prev) => prev + 1);
    }
  };

  const getQuestionText = () => {
    switch (currentQuestionIndex) {
      case 0:
        return "What digit is in the hundreds place?";
      case 1:
        return "If you swap the tens and hundreds digits, what number do you get?";
      default:
        return "";
    }
  };

  return (
    <div
      style={{
        fontFamily: "Fredoka One, cursive",
        background: "linear-gradient(120deg,rgb(86, 248, 54),rgb(235, 56, 139))",
        height: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <div
        style={{
          background: "white",
          borderRadius: 20,
          boxShadow: "0 10px 30px rgba(0, 150, 136, 0.3)",
          padding: "30px 40px",
          width: 420,
          textAlign: "center",
          transition: "transform 0.3s ease",
        }}
      >
        <h2 style={{ color: "#00796b", marginBottom: 15 }}>Place Value Puzzle</h2>

        {/* 🎭 Avatar reacts */}
        <img
          src={
            feedbackClass === "correct"
              ? 
              'https://avataaars.io/?avatarStyle=Transparent&topType=WinterHat3&accessoriesType=Kurt&hatColor=Gray01&facialHairType=MoustacheMagnum&facialHairColor=Red&clotheType=BlazerShirt&eyeType=Side&eyebrowType=SadConcernedNatural&mouthType=Grimace&skinColor=Yellow'
              // "https://avataaars.io/?avatarStyle=Circle&topType=ShortHair&eyeType=Happy&mouthType=Smile"
              : feedbackClass === "incorrect"
              ? "https://avataaars.io/?avatarStyle=Transparent&topType=WinterHat3&accessoriesType=Kurt&hatColor=Gray01&facialHairType=MoustacheMagnum&facialHairColor=Red&clotheType=BlazerShirt&eyeType=Side&eyebrowType=SadConcernedNatural&mouthType=Tongue&skinColor=Yellow"
              : "https://avataaars.io/?avatarStyle=Transparent&topType=WinterHat3&accessoriesType=Kurt&hatColor=Gray01&facialHairType=MoustacheMagnum&facialHairColor=Red&clotheType=BlazerShirt&eyeType=Side&eyebrowType=SadConcernedNatural&mouthType=Tongue&skinColor=Yellow"
          }
          alt="character"
          style={{
            width: 120,
            height: 120,
            margin: "0 auto 20px",
            transition: "0.3s ease",
          }}
        />

        {/* Number Display */}
        <div
          style={{
            fontSize: "3rem",
            color: "#004d40",
            marginBottom: 30,
            letterSpacing: "0.2em",
          }}
        >
          {currentNumberStr.split("").join(" ")}
        </div>

        {/* Question */}
        <div
          style={{
            fontSize: "1.3rem",
            marginBottom: 8,
            color: "#00695c",
            minHeight: 30,
          }}
        >
          {getQuestionText()}
        </div>

        {/* Input */}
        <input
          type="text"
          value={userAnswer}
          onChange={(e) => setUserAnswer(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") checkAnswer();
          }}
          style={{
            fontSize: "1.5rem",
            padding: "8px 15px",
            borderRadius: 12,
            border: "3px solid #004d40",
            width: "100%",
            textAlign: "center",
            outline: "none",
            marginBottom: 20,
          }}
        />

        {/* Buttons */}
        <div>
          {feedbackClass !== "correct" ? (
            <button
              onClick={checkAnswer}
              style={{
                padding: "12px 35px",
                borderRadius: 25,
                background: "#00796b",
                color: "white",
                fontSize: "1.2rem",
                cursor: "pointer",
                border: "none",
                marginBottom: 10,
              }}
            >
              Submit
            </button>
          ) : (
            <button
              onClick={nextQuestion}
              style={{
                padding: "12px 35px",
                borderRadius: 25,
                background: "#004d40",
                color: "white",
                fontSize: "1.2rem",
                cursor: "pointer",
                border: "none",
                marginBottom: 10,
              }}
            >
              Next
            </button>
          )}
        </div>

        {/* Feedback */}
        <div
          style={{
            marginTop: 22,
            fontSize: "1.3rem",
            fontWeight: 700,
            color: feedbackClass === "correct" ? "#2e7d32" : "#b71c1c",
            minHeight: 40,
          }}
        >
          {feedback}
        </div>
      </div>
    </div>
  );
}
